/**
 * The seams that keep the sync core framework-free.
 *
 * ADR-0001 §3.1 commits to writing the sync engine, data model, and API client as plain
 * TypeScript with no React and no storage-library imports, so that the named escape
 * hatch in §4.8 (React Native, if iOS photo loss forces it) costs us the UI shell and
 * nothing else. These interfaces are that commitment, expressed in code: the engine
 * talks to storage and network only through them.
 *
 * The production implementations live outside this package — Dexie/IndexedDB in the PWA,
 * `fetch` in the API client — and an in-memory implementation of `OutboxStore` ships in
 * `testing.ts` so the engine's behaviour is testable without a browser.
 */

import type { Uuid } from './ids.js';
import type { OpError, PullChangesRequest, PullChangesResponse, PushOpsRequest, PushOpsResponse, SyncOp } from './protocol.js';

/**
 * Lifecycle of a queued op.
 *
 * `pending` → `synced` is the only path that discards the client's obligation. Nothing
 * else may. ADR-0001 §4.2 step 6: mark synced on server confirmation, never before.
 */
export type OutboxStatus = 'pending' | 'synced' | 'failed';

export interface OutboxRecord {
  readonly op: SyncOp;
  /**
   * Local insertion order. Monotonic per device. This is what FIFO means here — we do
   * not sort by any clock, including the one baked into the op id.
   */
  readonly localSeq: number;
  readonly status: OutboxStatus;
  readonly attemptCount: number;
  /** Epoch ms before which this op should not be retried. Backoff. */
  readonly nextAttemptAt: number;
  /** Server sequence assigned on success. Absent until confirmed. */
  readonly serverSeq?: number;
  readonly lastError?: OpError;
}

/**
 * Durable local queue.
 *
 * Every method must be crash-safe: the device can lose power between any two calls, and
 * the correct outcome of that is a redundant resend (harmless, `opId` dedupes it), never
 * a dropped op.
 */
export interface OutboxStore {
  /** Appends a new op. Assigns `localSeq`. Must be atomic with the caller's local write. */
  enqueue(op: SyncOp): Promise<OutboxRecord>;

  /**
   * Oldest-first pending ops eligible to send now.
   *
   * Implementations must exclude ops whose `nextAttemptAt` is in the future, and ops
   * belonging to a quarantined entity.
   */
  listSendable(now: number, limit: number): Promise<readonly OutboxRecord[]>;

  /** Confirmed by the server. Terminal, and the only status that releases the op. */
  markSynced(opId: Uuid, serverSeq: number): Promise<void>;

  /** Transient failure. Bumps `attemptCount` and sets the backoff deadline. */
  markRetry(opId: Uuid, nextAttemptAt: number, error: OpError): Promise<void>;

  /** Permanent rejection. The op will not be resent without operator action. */
  markFailed(opId: Uuid, error: OpError): Promise<void>;

  /**
   * Stops further ops for one entity from being sent.
   *
   * Ordering within an entity is load-bearing: an `update` applied on top of a `create`
   * that the server rejected would write evidence against a base that does not exist.
   * When an op fails terminally we freeze its entity rather than skipping past it.
   */
  quarantineEntity(entityId: Uuid, reason: OpError): Promise<void>;

  /** Ops not yet confirmed by the server. Drives the "N not yet uploaded" indicator. */
  countUnsynced(): Promise<number>;
}

/** Network seam. One implementation over `fetch`; a fake in tests. */
export interface SyncTransport {
  pushOps(request: PushOpsRequest): Promise<PushOpsResponse>;
  pullChanges(request: PullChangesRequest): Promise<PullChangesResponse>;
}

/** Injected so tests do not sleep and do not depend on the host clock. */
export interface Clock {
  now(): number;
}

export const systemClock: Clock = { now: () => Date.now() };
